import React, { useEffect, useMemo, useState } from "react";
import useP2PQuotes from "../hooks/useP2PQuotes";
import styles from "./P2PPrices.module.css";

const TYPE_OPTIONS = [
  { label: "Comprar USDT", side: "SELL" },
  { label: "Vender USDT", side: "BUY" },
];

const formatCurrency = (value, fiat) => {
  if (typeof value !== "number" || Number.isNaN(value)) return "—";
  return Number(value).toLocaleString("pt-BR", {
    style: "currency",
    currency: fiat,
    minimumFractionDigits: 2,
  });
};

export default function P2PPrices() {
  const [side, setSide] = useState(TYPE_OPTIONS[0].side);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const { data, loading, hasFailures, refresh } = useP2PQuotes({ side, limit: 10 });

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const interval = setInterval(() => refresh(), 60000);
    return () => clearInterval(interval);
  }, [autoRefresh, refresh]);

  const statusText = loading
    ? "Atualizando..."
    : hasFailures
    ? "Algumas fontes falharam (ver console)"
    : "Dados atualizados";

  const cards = useMemo(
    () =>
      data.map((card) => {
        const buyQuote = card.sellQuote;
        const sellQuote = card.buyQuote;
        const timestamp =
          buyQuote?.fetchedAt || sellQuote?.fetchedAt
            ? new Date(buyQuote?.fetchedAt || sellQuote?.fetchedAt).toLocaleString()
            : "—";
        const buyActive = side === "SELL";
        const sellActive = side === "BUY";
        const buyMeta = buyQuote
          ? `Mediana: ${formatCurrency(buyQuote.median, card.fiat)} • ${buyQuote.samples} amostras`
          : "Sem dados";
        const sellMeta = sellQuote
          ? `Mediana: ${formatCurrency(sellQuote.median, card.fiat)} • ${sellQuote.samples} amostras`
          : "Sem dados";
        return (
          <div key={`${card.exchange}-${card.fiat}`} className={styles.card}>
            <div className={styles.cardHeader}>
              <strong>{card.label}</strong>
              <span>{`${card.exchange}.com`}</span>
            </div>
            <div className={styles.chipRow}>
              {card.experimental ? <span className={styles.experimental}>Experimental</span> : null}
              <span className={styles.cardMeta}>{card.fiat}</span>
            </div>
            <div className={styles.rateList}>
              <div
                className={`${styles.rateRow} ${styles.buy} ${
                  buyActive ? styles.active : ""
                }`}
              >
                <div>
                  <div className={styles.rowLabel}>Compra</div>
                  <div className={styles.rowMeta}>{buyMeta}</div>
                </div>
                <div className={`${styles.rowValue} ${styles.buy}`}>
                  {formatCurrency(buyQuote?.best, card.fiat)}
                </div>
              </div>
              <div
                className={`${styles.rateRow} ${styles.sell} ${
                  sellActive ? styles.active : ""
                }`}
              >
                <div>
                  <div className={styles.rowLabel}>Venda</div>
                  <div className={styles.rowMeta}>{sellMeta}</div>
                </div>
                <div className={`${styles.rowValue} ${styles.sell}`}>
                  {formatCurrency(sellQuote?.best, card.fiat)}
                </div>
              </div>
            </div>
            <div className={styles.footer}>
              <span>Atualizado: {timestamp}</span>
            </div>
          </div>
        );
      }),
    [data, side]
  );

  return (
    <section className={styles.container}>
      <div className={styles.actions}>
        <div className={styles.buttonGroup}>
          {TYPE_OPTIONS.map((option) => (
            <button
              key={option.side}
              type="button"
            className={`${styles.typeButton} ${
              side === option.side ? styles.typeButtonActive : ""
            }`}
              onClick={() => setSide(option.side)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className={styles.actionsRight}>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={() => refresh()}
          >
            Atualizar
          </button>
          <label className={styles.toggleAuto}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(event) => setAutoRefresh(event.target.checked)}
            />
            Auto-refresh (60s)
          </label>
        </div>
      </div>
      <p className={styles.loadingBanner}>{statusText}</p>
      <div className={styles.cards}>{cards}</div>
    </section>
  );
}
